<template>
  <NewSecretGeneratedModal
    v-if="createdWebhookId"
    :webhook-id="createdWebhookId"
    @closed="openCreatedWebhook"
  />
  <WebhookConfigModal
    v-else
    :initial-event-types="initialEventTypes"
    @webhook-created="handleWebhookCreated"
    @closed="handleConfigClosed"
  />
</template>

<script setup lang="ts">
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { ref } from 'vue';
import { useRoute } from 'vue-router';

// TODO: move these into pages/api/components once temp_api_controls is removed
import NewSecretGeneratedModal from '@/components/integrations/NewSecretGeneratedModal.vue';
import WebhookConfigModal from '@/components/integrations/WebhookConfigModal.vue';

import { useFeatureFlagStore } from '@/stores';
import type { WebhookEventType } from '@/types/webhooks';

import useApiControlsTracking from '../useApiControlsTracking';
import useApiRoutes from '../useApiRoutes';

const emit = defineEmits<{
  closed: [];
}>();

useHead({ title: 'Create Webhook - Webhooks - API - Crossbeam' });

const createdWebhookId = ref<string | null>(null);

const { openDetail } = useApiRoutes();
const route = useRoute();
const { hasFeatureFlag } = useFeatureFlagStore();
const { trackApiControlAction } = useApiControlsTracking();

const hasGreenfieldDealWebhooks =
  hasFeatureFlag('temp_genpipe_greenfields') &&
  hasFeatureFlag('temp_greenfield_deal_webhooks');

const initialEventTypes: WebhookEventType[] =
  route.query.webhookSource && hasGreenfieldDealWebhooks
    ? ['partner_greenfield_deal_closed_won']
    : [];

function handleWebhookCreated({ webhookId }: { webhookId: string }) {
  createdWebhookId.value = webhookId;
  trackApiControlAction({
    eventSite: EVENT_SITES.API_WEBHOOK_CREATE_MODAL,
    type: 'create_submitted',
    resource: 'webhook',
    resourceId: webhookId,
  });
}

function openCreatedWebhook() {
  if (!createdWebhookId.value) return;
  trackApiControlAction({
    eventSite: EVENT_SITES.API_WEBHOOK_SECRET_MODAL,
    type: 'secret_acknowledged',
    resource: 'webhook',
    resourceId: createdWebhookId.value,
  });
  openDetail('webhooks', createdWebhookId.value, { replace: true });
}

/**
 * WebhookConfigModal closes itself after a successful create, so only treat
 * this as a cancel when no webhook came back.
 */
function handleConfigClosed() {
  if (!createdWebhookId.value) emit('closed');
}
</script>
